// Builds dial options and color assignments for library host customization

import { useMemo } from 'react';
import {
  PLAYER_COLOR_FAMILIES,
  getColorStop,
} from '../../../styles/playerColors';

export type LibraryPoppyDialOption = {
  id: string;
  label: string;
  color: string;
};

interface RawOption {
  id: string;
  label: string;
}

interface UseLibraryPoppyDialOptionsArgs {
  personalityOptionsRaw: RawOption[];
  accentOptionsRaw: RawOption[];
  genderOptionsRaw: RawOption[];
}

interface UseLibraryPoppyDialOptionsResult {
  personalityOptions: LibraryPoppyDialOption[];
  accentOptions: LibraryPoppyDialOption[];
  genderOptions: LibraryPoppyDialOption[];
}

const colorStride = 3;
const buildColorOrder = (colors: string[]) =>
  colors.map((_, index) => colors[(index * colorStride) % colors.length]);
const dialColorValues = PLAYER_COLOR_FAMILIES.map((family) =>
  getColorStop(family, '900')
);

export function useLibraryPoppyDialOptions({
  personalityOptionsRaw,
  accentOptionsRaw,
  genderOptionsRaw,
}: UseLibraryPoppyDialOptionsArgs): UseLibraryPoppyDialOptionsResult {
  const optionColorMap = useMemo(() => {
    const colorOrder = buildColorOrder(dialColorValues);
    const optionKeys = [
      ...personalityOptionsRaw.map((option) => `personality:${option.id}`),
      ...accentOptionsRaw.map((option) => `accent:${option.id}`),
      ...genderOptionsRaw.map((option) => `gender:${option.id}`),
    ];
    const map = new Map<string, string>();
    optionKeys.forEach((key, index) => {
      map.set(key, colorOrder[index % colorOrder.length]);
    });
    return map;
  }, [personalityOptionsRaw, accentOptionsRaw, genderOptionsRaw]);

  const personalityOptions = useMemo(
    () =>
      personalityOptionsRaw.map((option) => ({
        ...option,
        color:
          optionColorMap.get(`personality:${option.id}`) ?? dialColorValues[0],
      })),
    [personalityOptionsRaw, optionColorMap]
  );
  const accentOptions = useMemo(
    () =>
      accentOptionsRaw.map((option) => ({
        ...option,
        color: optionColorMap.get(`accent:${option.id}`) ?? dialColorValues[0],
      })),
    [accentOptionsRaw, optionColorMap]
  );
  const genderOptions = useMemo(
    () =>
      genderOptionsRaw.map((option) => ({
        ...option,
        color: optionColorMap.get(`gender:${option.id}`) ?? dialColorValues[0],
      })),
    [genderOptionsRaw, optionColorMap]
  );

  return { personalityOptions, accentOptions, genderOptions };
}
